<template>
	<BaseLayout :footer-full="true">
		<div class="container">
			<h1 class="heading-1 text-align-center mb-100 mb-200-md">Fundrise Invitation Program</h1>
			<section class="grid-md">
				<article class="col-8-md col-offset-2-md position-relative">
					<div class="module">
						<div class="module-header">
							<h2 class="heading-5">Important Disclosure for Invitees</h2>
						</div>
						<div class="module-section">
							<p>
								The individual who invited you to become a client of Fundrise is (a) currently a client
								of Fundrise and (b) will potentially receive compensation, as more fully described
								below, if you participate in the Fundrise Invitation Program. As a result of the
								foregoing, such individual has a conflict of interest in inviting you to open a
								Qualifying New Account on Fundrise (as described more fully below).
							</p>
						</div>
					</div>
					<div class="module">
						<div class="module-header">
							<h2 class="heading-5">Terms and Conditions</h2>
						</div>
						<div class="module-section">
							<p>
								You must agree to the following terms and conditions in order to receive a Share Voucher
								(as defined below) for inviting your friends who might be interested in our service.
								These terms and conditions are in addition to your existing Fundrise Client Agreement
								with Fundrise Advisors, LLC (“Fundrise Advisors”).
							</p>
							<p>
								Through the Fundrise Invitation Program you can invite friends to sign up for an account
								on the "Fundrise Platform" (e.g., www.fundrise.com and its affiliated mobile apps) by
								sending them an invitation. Please send invites only to people you know personally who
								will be glad to get them. Fundrise will send one invite email on your behalf to each
								friend you invite via the Invitation Program email sending interface on
								fundrise.com/invitation-program/landing: (1) in your name; and/or (2) with your name.
								You will also be provided with a unique link that you may share with your friends.
							</p>
							<p>
								For each "Qualifying New Account on Fundrise" (as defined below) Fundrise Advisors will
								give you a Share Voucher that you may redeem when subscribing for shares of a designated
								Fundrise-sponsored public fund (the “Share Voucher”) according to the chart below, as
								determined by Fundrise Advisors. For the sake of clarity, neither you nor your invitee
								need to take any economic activity related to Fundrise Advisors (e.g., invest in
								Fundrise's programs) in order for you to receive a Share Voucher and you will not
								receive any additional Share Vouchers in the event your invitee ultimately becomes a
								client of Fundrise Advisors. The Share Voucher will be provided to you within
								approximately one week after all conditions are satisfied.
							</p>
							<p>
								In addition, if any of the individuals you invite ultimately become a client of Fundrise
								Advisors before the end of the invitation period (as described below), Fundrise Advisors
								will also provide them with, at a minimum, a Share Voucher with the same value that is
								provided to you, as determined by Fundrise Advisors. The Share Voucher will be provided
								to your friend within approximately one week after such friend has become a client of
								Fundrise Advisors.
							</p>
							<p>
								The aggregate value of Share Vouchers you can receive is capped at $900 in any given
								12-month period; there is no purchase or investment necessary by you or your invited
								friends in order for you to receive the Share Voucher. Note, however, that you accrue no
								additional benefit if your invited friend ultimately becomes a client of Fundrise
								Advisors or otherwise makes an investment.
							</p>
							<table v-if="invitationShareBonus.length > 0" class="table mt-100">
								<thead>
									<tr>
										<th>Inviter Account Value</th>
										<th class="text-align-right">Invitation Sender Share Voucher</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(share, index) in invitationShareBonus" :key="index">
										<td>{{ share.accountLevel }}</td>
										<td class="text-align-right">{{ share.bonus }}</td>
									</tr>
								</tbody>
							</table>
							<p>
								Subject to the limitations above, a "Qualifying New Account on Fundrise" is a new
								individual account on the Fundrise Platform by your Fundrise invited friend using the
								special designated link from your invitation, which your invited friend links to a
								supported external funding source within 30 days of confirming receipt of the invite
								link by providing their email address on the Fundrise Platform (the "invitation
								period"). Fundrise is not responsible for incorrect entry or other failure on the part
								of your invited friends to meet the standards of a Qualifying New Account on Fundrise.
							</p>
							<p>
								You understand we will inform your friend you are a client of our firm and that we are
								providing you with a Share Voucher as a result of the invitation. We will not reveal any
								other information about you or your account(s) on the Fundrise Platform. All existing
								terms of our Privacy Policy and our privacy obligations to you shall apply.
							</p>
							<p>
								Not all invitations will be considered valid. For example, if the external funding
								source is not supported, the individual account created will not be deemed to be a valid
								"Qualifying New Account." In addition, your friend(s) may receive multiple invitations
								because we have clients who provide us with duplicative information for invitations, and
								we can't promise your friend(s) will respond to the invitation based on the information
								we receive from you. Also, your friend(s) may decide independently to open an account on
								the Fundrise Platform, or they might already have an account on the Fundrise Platform
								when they receive the invitation. We might also change the Invitation Program in the
								future for existing and/or new clients. We reserve the right, in our sole discretion, to
								determine that a Share Voucher is not warranted, and/or to change the terms of the
								Invitation Program with or without notice to you.
							</p>
							<p>
								This Invitation Program is not valid with any other offers and is non-transferrable.
								Offer available to U.S. residents only. Fundrise Advisors reserves the right to
								terminate this offer at any time for any reason, to limit the value of the Share
								Vouchers you are eligible to receive, and to refuse or recover any Share Voucher if
								Fundrise Advisors determines that it was obtained under wrongful or fraudulent
								circumstances, that inaccurate or incomplete information was provided in opening an
								account on the Fundrise Platform or connecting a funding source, that any rules or
								regulations would be violated, or that any terms of the Fundrise Client Agreements have
								been violated.
							</p>
							<p>
								Offer not available for IRA accounts. However, for the avoidance of doubt, this offer is
								available to IRA investors that have investments in a separate non-IRA account on the
								Fundrise Platform.
							</p>
							<p>
								By accepting these terms and conditions and participating in this Invitation Program you
								acknowledge certain information so that we can ensure your eligibility. Specifically,
								you represent to us that:
							</p>
							<ol class="ol ml-30">
								<li>
									You aren't otherwise in the investment advisory or brokerage business (or, if you
									are, then your job function is not related to this invitation activity or to the
									financial management of your friend(s)' assets), and you will not provide investment
									advice to any individual in connection with this Invitation Program;
								</li>
								<li>
									you aren't a government entity, or an agent of such entity acting on its behalf;
								</li>
								<li>
									you will comply with the requirements for this Invitation Program described here,
									and any securities laws that may apply;
								</li>
								<li>
									you are not currently, and have at no time in the past ten (10) years, been the
									subject of: (i) an SEC opinion or order barring, suspending, or prohibiting you from
									acting in any capacity under the federal securities laws; (ii) a conviction by court
									of competent jurisdiction within the United States of any felony or misdemeanor
									involving conduct described in paragraph (2)(A) through (D) of section 203(e) of the
									Investment Advisers Act of 1940 (the “Advisers Act”); (iii) a conviction by a court
									of competent jurisdiction within the United States of engaging in, any of the
									conduct specified in paragraphs (1), (5), or (6) of section 203(e) of the Advisers
									Act; (iv) the entry of any final order by any entity described in paragraph (9)
									section 203(e) of the Advisers Act, or by the U.S. Commodity Futures Trading
									Commission or a self-regulatory organization (as defined in the Form ADV Glossary of
									Terms) of the type described in paragraph (9) of section 203(e) of the Advisers Act;
									(v) the entry of an order, judgment or decree that is described in paragraph (4) of
									section 203(e) of the Advisers Act that is in effect at the time of such
									dissemination by any court of competent jurisdiction within the United States; or
									(vi) an SEC order that you cease and desist from committing or causing a violation
									or future violation of (a) any scienter-based anti-fraud provision of federal
									securities law, including without limitation section 17(a)(1) of the Securities Act
									of 1933 (“Securities Act”), sections 10(b) and 15(c)1 of the Securities Exchange Act
									of 1934, section 206(1) of the Advisers Act, any other rule or regulation thereunder
									or (b) Section 5 of the Securities Act. We may conduct due diligence on you to
									confirm any of the above information; and
								</li>
								<li>you will promptly notify us if any of items 1) through 4) above change.</li>
							</ol>
						</div>
					</div>
				</article>
			</section>
		</div>
	</BaseLayout>
</template>

<script setup lang="ts">
import { AccountLevelShareBonus } from 'types/account';
import { useHead } from '@unhead/vue';

const invitationShareBonus: Array<AccountLevelShareBonus> = [
	{
		accountLevel: '$10 - $999',
		bonus: '$25'
	},
	{
		accountLevel: '$1,000 - $99,999',
		bonus: '$50'
	},
	{
		accountLevel: '$100,000+',
		bonus: '$100'
	}
];

function setup(): void {
	useHead({
		title: 'Fundrise Invitation Program - Terms and Conditions',
		meta: [
			{ property: 'og:title', content: 'Fundrise Invitation Program - Terms and Conditions' },
			{
				name: 'description',
				content: 'Fundrise Invitation Program - Terms and Conditions'
			},
			{
				property: 'og:description',
				content: 'Fundrise Invitation Program - Terms and Conditions'
			}
		]
	});
}
setup();
</script>
